/**
 * «Mismo comercio»: de unos movimientos nuevos sin categoría, los grupos de
 * comercios que siempre has puesto en la misma categoría, para que Gavi
 * pregunte una vez «¿los pongo ahí?» (Vadik, 19-oct-2026).
 *
 * Solo mira lo nuevo: lo que ya tenías apuntado no se toca nunca, porque
 * cambiarlo descuadraría meses ya cerrados.
 */

export type MovimientoComercio = {
  id: string;
  payee: string | null;
  category: string | null;
};

export type GrupoComercio = {
  payee: string;
  categoria: string;
  /** Los movimientos nuevos sin categoría de ese comercio. */
  ids: string[];
};

/** Hacen falta al menos estos anteriores con categoría para fiarse. */
export const MINIMO_HISTORIAL = 3;
/** Como mucho se ofrecen estos comercios (los de más movimientos). */
export const MAXIMO_GRUPOS = 5;

/**
 * Agrupa por comercio los `nuevos` sin categoría y deja los que tienen al
 * menos `MINIMO_HISTORIAL` movimientos anteriores con categoría, todos en la
 * misma. Si una sola vez fue distinta (el regalo comprado en Mercadona), ese
 * comercio no se ofrece. Los anteriores sin categoría no cuentan.
 */
export function gruposMismoComercio(
  nuevos: MovimientoComercio[],
  anteriores: MovimientoComercio[],
): GrupoComercio[] {
  const idsNuevos = new Set(nuevos.map(m => m.id));
  const porComercio = new Map<string, string[]>();
  for (const m of nuevos) {
    if (m.payee != null && m.category == null) {
      porComercio.set(m.payee, [...(porComercio.get(m.payee) ?? []), m.id]);
    }
  }

  const categorias = new Map<string, Set<string>>();
  const cuantos = new Map<string, number>();
  for (const m of anteriores) {
    if (
      m.payee == null ||
      m.category == null ||
      idsNuevos.has(m.id) ||
      !porComercio.has(m.payee)
    ) {
      continue;
    }
    categorias.set(
      m.payee,
      (categorias.get(m.payee) ?? new Set()).add(m.category),
    );
    cuantos.set(m.payee, (cuantos.get(m.payee) ?? 0) + 1);
  }

  const grupos: GrupoComercio[] = [];
  for (const [payee, ids] of porComercio) {
    const cats = categorias.get(payee);
    if (cats?.size === 1 && (cuantos.get(payee) ?? 0) >= MINIMO_HISTORIAL) {
      grupos.push({ payee, categoria: [...cats][0], ids });
    }
  }
  return grupos
    .sort((a, b) => b.ids.length - a.ids.length)
    .slice(0, MAXIMO_GRUPOS);
}
